// Dashboard « Commandes récentes ».
// Desktop: the table, unchanged. Phone (<768px): the table hid the client and
// the formule and left a sideways-scrolling strip, so each order becomes one
// tappable row (≥44px) leading to the same order page as the table's link.
import { Link } from 'react-router-dom';
import type { Order } from '@/lib/api';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { formatPrice, formatDate } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { useIsPhone } from '@/lib/phoneViewport';

export function RecentOrdersCard({ orders: recentOrders }: { orders: Order[] }) {
  const { t } = useT();
  const phone = useIsPhone();
  return (
    <div className="bg-white rounded-xl border border-gray-100">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">{t('Commandes récentes')}</h3>
        <Link to="/admin/orders" className="inline-flex items-center min-h-11 md:min-h-0 -my-3 md:my-0 text-xs text-brand-500 font-medium hover:underline">
          {t('Voir toutes')} →
        </Link>
      </div>
      {phone ? (
        recentOrders.length === 0 ? (
          <p className="px-5 py-8 text-center text-gray-400 text-sm">{t('Aucune commande récente')}</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {recentOrders.map((order) => (
              <li key={order.id}>
                <Link
                  data-recent-order
                  to={`/admin/orders/${order.shopify_order_id}`}
                  className="flex items-center gap-3 min-h-11 px-4 py-3 active:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-brand-600 shrink-0">{order.shopify_order_name}</span>
                      {order.bundle && (
                        <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium shrink-0">{order.bundle}</span>
                      )}
                    </p>
                    <p className="text-[13px] text-gray-700 truncate">{order.customer_name ?? '—'}</p>
                    <p className="text-[11px] text-gray-400">{formatDate(order.created_at)}</p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    <StatusBadge status={order.status} />
                    <span className="text-sm font-semibold text-gray-800 tabular-nums">{formatPrice(order.total_price)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">{t('Commande')}</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wide hidden md:table-cell">{t('Client')}</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wide hidden sm:table-cell">{t('Formule')}</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">{t('Statut')}</th>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wide">{t('Prix')}</th>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wide hidden lg:table-cell">{t('Date')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {recentOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-400 text-sm">
                    {t('Aucune commande récente')}
                  </td>
                </tr>
              ) : recentOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <Link
                      to={`/admin/orders/${order.shopify_order_id}`}
                      className="font-semibold text-brand-500 hover:underline"
                    >
                      {order.shopify_order_name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell text-gray-700 font-medium">
                    {order.customer_name ?? '—'}
                  </td>
                  <td className="px-5 py-3 hidden sm:table-cell">
                    {order.bundle && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                        {order.bundle}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-800">
                    {formatPrice(order.total_price)}
                  </td>
                  <td className="px-5 py-3 text-right text-gray-400 hidden lg:table-cell">
                    {formatDate(order.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
